import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { buildFilteredHighlightedProductsV2 } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import UIUAFeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import ProductIcon from '../../../components/common/ProductIcon';
import FeatureTableColTag from '../../../components/feature-table/FeatureTableColTag';
import { SingleSkeletonLoader } from '../../components/SingleSkeletonLoader';
import { FeatureTableHeaderCellContent } from '../../components/feature-table/FeatureTableHeaderCellContent';
import { skuCategoryNameToBundleTypeOrProductLine } from '../../constants';
import { PriceByProductApiName } from '../../containers/PriceByProductApiName';
import { PurchaseMotionsByProductApiNameAndLocation } from '../../containers/PurchaseMotionsByProductApiNameAndLocation';
import { useBadges } from '../../hooks/badges/useBadges';
import { usePinnedFeaturesBySkuCategoryName } from '../../hooks/features/useFeatures';
import { createFeatureTableHeaderTrackingData } from '../../utils/trackingData';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LabelContent = ({
  skuCategoryName
}) => {
  const productLine = skuCategoryNameToBundleTypeOrProductLine[skuCategoryName];
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(ProductIcon, {
      name: productLine,
      iconStyle: {
        inlineSize: '24px',
        marginInlineEnd: '300'
      }
    }), /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-300",
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "suiteFeatureTable.section.title",
        options: {
          product: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `productLine.${productLine}`
          })
        }
      })
    })]
  });
};
export const MarketingPlusFeatureTable = () => {
  var _badges$marketingPlu, _badges$marketingPlu2;
  const apiNames = ['marketing-plus-professional-bundle', 'marketing-plus-enterprise-bundle'];
  const {
    badges
  } = useBadges({
    productApiNames: apiNames
  });

  // Marketing Plus contains Marketing Hub + Content Hub (CMS)
  const productLevels = ['PROFESSIONAL', 'ENTERPRISE'];
  const productLinesFromBundle = ['MARKETING', 'CMS'];

  // Map product lines to SKU categories with their API names
  const skuCategoryNameToApiName = {
    MARKETING_PRODUCTS: buildFilteredHighlightedProductsV2({
      productLine: 'MARKETING',
      productLevels
    }),
    CMS_HUB_PRODUCTS: buildFilteredHighlightedProductsV2({
      productLine: 'CMS',
      productLevels
    })
  };
  const {
    featuresBySkuCategoryName,
    loading,
    error
  } = usePinnedFeaturesBySkuCategoryName(skuCategoryNameToApiName);
  if (loading) {
    return /*#__PURE__*/_jsx(SingleSkeletonLoader, {
      height: 250,
      width: "100%"
    });
  }
  if (error) {
    return null;
  }
  if (!featuresBySkuCategoryName || !Object.keys(featuresBySkuCategoryName).length) {
    return null;
  }

  // Build section data for each product line (Marketing and Content Hub)
  const skuCategoryNamesFromBundle = productLinesFromBundle.map(productLine => productLine === 'MARKETING' ? 'MARKETING_PRODUCTS' : 'CMS_HUB_PRODUCTS');
  const sectionData = skuCategoryNamesFromBundle.reduce((acc, skuCategoryName) => {
    acc[skuCategoryName] = {
      features: featuresBySkuCategoryName[skuCategoryName] || [],
      LabelContent: /*#__PURE__*/_jsx(LabelContent, {
        skuCategoryName: skuCategoryName
      }),
      apiNames: skuCategoryNameToApiName[skuCategoryName] || []
    };
    return acc;
  }, {});
  const bodyData = {
    sharedData: {
      subscreen: 'MARKETING_PLUS'
    },
    sectionData
  };

  // Build header data for the two bundle tiers
  const headerData = {
    ['marketing-plus-professional-bundle']: {
      Content: /*#__PURE__*/_jsx(FeatureTableHeaderCellContent, {
        Badge: /*#__PURE__*/_jsx(FeatureTableColTag, {
          apiName: 'marketing-plus-professional-bundle',
          badgeName: badges === null || badges === void 0 || (_badges$marketingPlu = badges['marketing-plus-professional-bundle']) === null || _badges$marketingPlu === void 0 ? void 0 : _badges$marketingPlu.badgeName
        }),
        ProductDisplayName: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "pricing-pages-ui.merchCardsV2.titleDisplay.professionalCard"
        }),
        Price: /*#__PURE__*/_jsx(PriceByProductApiName, {
          productApiName: 'marketing-plus-professional-bundle'
        }),
        PurchaseMotion: /*#__PURE__*/_jsx(PurchaseMotionsByProductApiNameAndLocation, {
          productApiName: 'marketing-plus-professional-bundle',
          location: "MERCH_CARD",
          displayCount: 1,
          trackingData: createFeatureTableHeaderTrackingData('marketing-plus-professional-bundle', 'marketing-plus')
        })
      })
    },
    ['marketing-plus-enterprise-bundle']: {
      Content: /*#__PURE__*/_jsx(FeatureTableHeaderCellContent, {
        Badge: /*#__PURE__*/_jsx(FeatureTableColTag, {
          apiName: 'marketing-plus-enterprise-bundle',
          badgeName: badges === null || badges === void 0 || (_badges$marketingPlu2 = badges['marketing-plus-enterprise-bundle']) === null || _badges$marketingPlu2 === void 0 ? void 0 : _badges$marketingPlu2.badgeName
        }),
        ProductDisplayName: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "pricing-pages-ui.merchCardsV2.titleDisplay.enterpriseCard"
        }),
        Price: /*#__PURE__*/_jsx(PriceByProductApiName, {
          productApiName: 'marketing-plus-enterprise-bundle'
        }),
        PurchaseMotion: /*#__PURE__*/_jsx(PurchaseMotionsByProductApiNameAndLocation, {
          productApiName: 'marketing-plus-enterprise-bundle',
          location: "MERCH_CARD",
          displayCount: 1,
          trackingData: createFeatureTableHeaderTrackingData('marketing-plus-enterprise-bundle', 'marketing-plus')
        })
      })
    }
  };
  return /*#__PURE__*/_jsx(UIUAFeatureTable, {
    headerData: headerData,
    bodyData: bodyData,
    canViewNewSeats: true,
    isInbound25PricingPage: true
  });
};